import type { WmsClassLegendItem } from "./wmsClassLegend";

export interface LayerUnit {
  sigla: string;
  nome?: string;
}

const GREEK: Record<string, string> = {
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  lambda: "λ",
  mu: "μ",
};

/**
 * Nosso GeoServer grava as siglas do SGB com as letras gregas transliteradas
 * (`NP3_gamma_2it45`, `C_cortado_2_gamma_b`); a legenda do ArcGIS usa os
 * caracteres originais (`NP3γ2it45`, `є2γb`).
 */
export function normalizeSigla(sigla: string): string {
  return sigla
    .replace(/C_cortado_/g, "є")
    .replace(/_(alpha|beta|gamma|delta|lambda|mu)_/g, (_, name) => GREEK[name]);
}

function labelSigla(label: string): string {
  const sep = label.indexOf(" - ");
  return (sep >= 0 ? label.slice(0, sep) : label).trim();
}

/**
 * Cruza a legenda do MapServer (`/legend?f=json`, que cobre o Brasil inteiro)
 * com as unidades presentes na camada. Unidades sem entrada no ArcGIS caem na
 * cor do SLD do nosso GeoServer, para não sumirem da legenda.
 */
export function buildArcgisClassLegend(
  arcgisJson: any,
  units: LayerUnit[],
  sldColors: Map<string, string>
): WmsClassLegendItem[] {
  const pending = new Map<string, LayerUnit>();
  units.forEach((unit) => {
    if (unit.sigla) pending.set(normalizeSigla(unit.sigla), unit);
  });

  const items: WmsClassLegendItem[] = [];
  const entries: any[] = (arcgisJson?.layers ?? []).flatMap(
    (layer: any) => layer?.legend ?? []
  );

  entries.forEach((entry) => {
    const label: string = entry?.label ?? "";
    const sigla = labelSigla(label);
    if (!pending.has(sigla) || !entry.imageData) return;
    pending.delete(sigla);
    items.push({
      label,
      imageUrl: `data:${entry.contentType ?? "image/png"};base64,${
        entry.imageData
      }`,
    });
  });

  pending.forEach((unit, sigla) => {
    const color = sldColors.get(unit.sigla);
    if (!color) return;
    items.push({ label: unit.nome ? `${sigla} - ${unit.nome}` : sigla, color });
  });

  return items;
}
